import { RecordChip } from 'components/record-chip/RecordChip';
import { CheckIcon, ChevronDownIcon } from 'components/icons';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import React, { Fragment, Ref, useRef } from 'react';
import { useIntl } from 'react-intl';
import { cn } from 'utils/cn';
import { isComposing } from 'utils/isComposing';
import type { PlaceholderSession } from '../hooks/usePlaceholderFill';
import PlaceholderAutocomplete, { aboveComposer, type SlotRecord } from './PlaceholderAutocomplete';

const PILL_CLASS_NAME =
    'text-meta inline-flex h-6 max-w-full items-center gap-1 rounded-md px-1.5 align-middle font-medium';
/** A slot still empty: dashed, with the slot's name as its hint. No warning colour; the composer says what is missing. */
const EMPTY_PILL_CLASS_NAME = cn(
    PILL_CLASS_NAME,
    'border-border-dashed bg-surface-subtle text-foreground-secondary border border-dashed'
);
const VALUE_PILL_CLASS_NAME = cn(PILL_CLASS_NAME, 'bg-neutral-tint text-foreground-secondary');

interface PlaceholderFillBarProps {
    ref?: Ref<HTMLDivElement>;
    session: PlaceholderSession;
    /** The phone: a record slot's picker is a sheet rather than a menu. */
    asSheet?: boolean;
    className?: string;
    onChange: (value: string) => void;
    onNext: () => void;
    onOptionSelect: (value: string) => void;
    onRecordSelect: (record: SlotRecord) => void;
    onDismiss: () => void;
    /** Enter once every slot is filled. */
    onSubmit: () => void;
}

/**
 * The composer's text while a ready-made prompt's slots are filled: the prompt as it will read, each slot inline. The
 * slot being filled is its control — a menu of the prompt's options, a record picker, or a field — a picked record
 * is its chip, and a slot not reached yet is a dashed pill. Escape (or a press outside) leaves the template as plain
 * text in the field, its empty slots still `[…]`.
 */
const PlaceholderFillBar: React.FC<PlaceholderFillBarProps> = ({
    ref,
    session,
    asSheet = false,
    className,
    onChange,
    onNext,
    onOptionSelect,
    onRecordSelect,
    onDismiss,
    onSubmit,
}) => {
    const intl = useIntl();
    const menuTrigger = useRef<HTMLButtonElement>(null);
    const { baseText, placeholders, currentIndex, values, records, optionsMap, placeholderInputTypes, noteId } =
        session;
    // Every slot filled and moved past: the prompt is ready to send.
    const isDone = currentIndex >= placeholders.length;

    const handleKeyDown = (event: React.KeyboardEvent) => {
        // A slot's menu or sheet is portalled: its keys reach here through React, and are its own.
        if (event.defaultPrevented || !event.currentTarget.contains(event.target as Node)) return;
        if (event.key === 'Escape') {
            event.preventDefault();
            onDismiss();
        } else if (event.key === 'Enter' && !event.shiftKey) {
            // An IME's Enter confirms the word being composed.
            if (isComposing(event)) return;
            event.preventDefault();
            if (isDone) {
                onSubmit();
            } else {
                onNext();
            }
        }
    };

    const renderCurrent = (name: string) => {
        const value = values[currentIndex];
        const inputType = placeholderInputTypes?.[name];
        if (inputType) {
            return (
                <PlaceholderAutocomplete
                    key={`${inputType}-${name}`}
                    inputType={inputType}
                    placeholder={name}
                    asSheet={asSheet}
                    onChange={onChange}
                    onPick={onRecordSelect}
                />
            );
        }
        const options = optionsMap?.[name];
        if (options?.length) {
            return (
                <DropdownMenu defaultOpen>
                    <DropdownMenuTrigger
                        ref={menuTrigger}
                        className={cn(
                            value ? VALUE_PILL_CLASS_NAME : EMPTY_PILL_CLASS_NAME,
                            'hit-area relative cursor-pointer'
                        )}
                    >
                        {value || name}
                        <ChevronDownIcon className="size-3.5" />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                        anchor={() => aboveComposer(menuTrigger.current)}
                        side="top"
                        sideOffset={6}
                        className="w-52"
                    >
                        <DropdownMenuGroup>
                            <DropdownMenuLabel className="text-label text-muted-foreground uppercase">
                                {intl.formatMessage({ id: 'ai-chat.sample-prompts.choose' })}
                            </DropdownMenuLabel>
                            {/* The slot's value is marked; picking it again moves on, as any pick does. */}
                            {options.map((option) => {
                                const current = option.value === value;
                                return (
                                    <DropdownMenuItem
                                        key={option.value}
                                        onClick={() => onOptionSelect(option.value)}
                                        className={cn(current && 'bg-primary-wash text-primary-ink justify-between')}
                                    >
                                        {option.label}
                                        {current ? <CheckIcon className="size-3.5" /> : null}
                                    </DropdownMenuItem>
                                );
                            })}
                        </DropdownMenuGroup>
                    </DropdownMenuContent>
                </DropdownMenu>
            );
        }
        return (
            <input
                autoFocus
                value={value}
                aria-label={name}
                placeholder={name}
                onChange={(event) => onChange(event.target.value)}
                onKeyDown={(event) => {
                    if (event.key === 'Tab' && !event.shiftKey) {
                        event.preventDefault();
                        onNext();
                    }
                }}
                className={cn(
                    PILL_CLASS_NAME,
                    'border-ring bg-card text-foreground shadow-focus-ring placeholder:text-muted-foreground w-40 border outline-none',
                    // 16px on the phone, or iOS zooms the page in as the field takes focus.
                    asSheet && 'text-base'
                )}
            />
        );
    };

    // The prompt's text with each `[name]` in its place; only the current slot's first appearance is its control.
    const segments = baseText.split(/\[([^\]]+)\]/g);
    const currentPartIndex = segments.findIndex(
        (part, partIndex) => partIndex % 2 === 1 && part === placeholders[currentIndex]
    );
    const parts = segments.map((part, partIndex) => {
        if (partIndex % 2 === 0) {
            return <Fragment key={partIndex}>{part}</Fragment>;
        }
        const index = placeholders.indexOf(part);
        const record = records[index];
        if (record) {
            return (
                <RecordChip
                    key={partIndex}
                    entityType={record.entityType}
                    id={record.id}
                    label={record.label}
                    partyNumber={record.partyNumber}
                    // A new tab, so the prompt being filled stays where it is.
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-meta h-6 align-middle"
                />
            );
        }
        if (partIndex === currentPartIndex) {
            return <Fragment key={partIndex}>{renderCurrent(part)}</Fragment>;
        }
        const value = values[index];
        return (
            <span key={partIndex} className={value ? VALUE_PILL_CLASS_NAME : EMPTY_PILL_CLASS_NAME}>
                {value || part}
            </span>
        );
    });

    return (
        // Focusable so the composer can hand it the cursor once every slot is filled, where Enter sends.
        <div
            ref={ref}
            tabIndex={-1}
            onKeyDown={handleKeyDown}
            className={cn('pe-2 leading-[1.85] outline-none', className)}
        >
            {parts}
            {noteId ? (
                <span className="text-caption text-muted-foreground block">{intl.formatMessage({ id: noteId })}</span>
            ) : null}
        </div>
    );
};

export default PlaceholderFillBar;
